import { Link } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { useFeaturedAvatar } from '@/hooks/useFeaturedAvatar';
import { ChromaKeyVideo } from '@/components/avatars/ChromaKeyVideo';
import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import { supportsVp9Alpha } from '@/lib/videoAlphaSupport';
import { cn } from '@/lib/cn';

/**
 * The player's QAvatar, floating over the app banner — bottom-left, head free
 * to rise above the banner's top edge.
 *
 * This used to be the Hub's alone. It now lives in `<AppBanner />`, so the
 * avatar holds the same spot on every screen, and it is the ONLY avatar: the
 * legacy Laravel picture circle is gone.
 *
 * Renders nothing when the player has no transparent avatar — a placeholder
 * (dragon egg) is a later job.
 */
export function BannerAvatar() {
  const { featured, clip, showVideo, showImage } = useFeaturedAvatar();
  // Compact banner has NO row above it, so the headroom the head normally rises
  // into is gone (the shell clips at the top). Shrink the avatar in compact so
  // the head stays below the clip line while the feet keep the same anchor.
  const compact = useAppSelector((s) => s.settings.bannerCompact);

  if (!showVideo && !showImage) return null;

  return (
    <>
      {/* Tap target — confined to the avatar's footprint on the LEFT of the
          banner, so taps near the right-side game selector never open
          /avatars. The visual is pointer-events-none, so this bounded link is
          the only thing that catches a tap; `peer` drives its press feedback.
          It must stay BEFORE the visual: `peer-*` only reaches later siblings. */}
      <Link
        to="/avatars"
        aria-label="Open avatars"
        className={cn(
          'peer absolute bottom-[-25%] left-0 z-[1] w-[46%]',
          compact ? 'h-[125%]' : 'h-[160%]',
        )}
      />
      {/* Decorative — never captures taps.
          Pushed DOWN and LEFT off the banner's bottom-left corner: anchored at
          bottom-0 the head (h-180%) reached ~66px above the banner and the
          screen edge clipped it. bottom-[-25%] drops it ~20px so the whole head
          fits the headroom the top row leaves; left-[-4%] pulls it ~13px left —
          out of the banner, but inside the banner's own 16px padding, so it
          never reaches the screen edge. Compact has no top row, so the height
          drops to h-140% to keep the head under the clip line. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute bottom-[-25%] left-[-4%] z-[1]',
          compact ? 'h-[140%]' : 'h-[180%]',
        )}
      >
        {showVideo ? (
          // Video output is 1:1 (768x768 from fal.ai providers) — no aspect
          // wrapper, which would distort the canvas. The canvas's intrinsic
          // width/height (set per-frame from video.videoWidth/Height) carries
          // the ratio; h-full + w-auto lets the browser preserve it.
          //
          // translate-x-[-30%] shifts the canvas leftward by 30% of its own
          //   width, pulling the subject flush with the left edge the static
          //   PNG path uses (the source frame has empty margins around it).
          // translate-y-[-4%] consumes the source frame's sub-feet padding so
          //   the feet land on the banner's bottom rather than below it.
          <div className="h-full w-auto translate-x-[-30%] translate-y-[-4%] drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)] transition-transform peer-active:scale-95">
            {clip!.matteRgbUrl && clip!.matteAlphaUrl ? (
              // H.264 dual-stream — universal. Edge decomposite removes the
              // backdrop tint from semi-transparent edges. Legacy clips carry
              // chromaKeyHex (magenta); new ones don't (veed pre-mattes RGB to
              // black), so black is the default.
              <DualStreamVideo
                key={`dual-${clip!.clipId}`}
                rgbUrl={clip!.matteRgbUrl}
                alphaUrl={clip!.matteAlphaUrl}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                knownBgHex={
                  // Veo always mattes without magenta preprocessing, so a stale
                  // chromaKeyHex would mis-decomposite the border pixels and
                  // amplify scene-colour residue into a halo. Force #000000 for
                  // Veo. See AvatarDetail effectiveBgHexFor() for the twin.
                  clip!.provider === 'veo31-fal' || clip!.provider === 'veo31-fast-fal'
                    ? '#000000'
                    : (clip!.chromaKeyHex ?? '#000000')
                }
              />
            ) : clip!.webmAlphaUrl && supportsVp9Alpha() ? (
              // Legacy WebM VP9-alpha — Chromium only.
              <video
                key={`webm-${clip!.clipId}`}
                src={clip!.webmAlphaUrl}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-contain"
              />
            ) : (
              <ChromaKeyVideo
                // key={clipId} forces a clean remount when the active clip
                // changes: React would otherwise reuse the same <video>, which
                // doesn't auto-reload on src changes — the old MP4 keeps playing.
                key={clip!.clipId}
                src={clip!.videoUrl!}
                poster={clip!.posterUrl ?? featured!.imageUrl}
                keyColor={clip!.chromaKeyHex ?? '#FF00FF'}
              />
            )}
          </div>
        ) : (
          <img
            src={featured!.imageUrl}
            alt=""
            className="h-full w-auto object-contain object-bottom drop-shadow-[0_8px_16px_rgba(0,0,0,0.4)] transition-transform peer-active:scale-95"
          />
        )}
      </div>
    </>
  );
}
